import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ArrowLeft, Bot, User, AlertTriangle, Sparkles, ClipboardCheck, XCircle } from "lucide-react";

export default function StaffAsistenteDetalle() {
  const { convId } = useParams();
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/agent/conversations/${convId}`).then((r) => setData(r.data));
  }, [convId]);

  if (!data) return <StaffLayout user={user} superadmin={superadmin}><p className="text-gray-500">Cargando...</p></StaffLayout>;

  const conv = data.conversation;
  const backTo = superadmin ? "/app/superadmin/asistente" : "/app/staff/asistente";

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <Link to={backTo} data-testid="back-btn"
          className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white mb-4">
          <ArrowLeft className="w-3 h-3" /> Volver
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Conversación</p>
            <h1 className="mt-1 text-2xl font-bold text-white font-display">
              {data.user?.email || <span className="text-gray-500 italic">anónimo</span>}
              {conv.escalated && (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 uppercase">
                  <AlertTriangle className="w-3 h-3" /> Escalada
                </span>
              )}
            </h1>
            <p className="mt-1 text-xs text-gray-500 font-mono">
              {conv.id} · {conv.messages_count} mensajes · creada {new Date(conv.created_at).toLocaleString("es-MX")}
            </p>
          </div>
          <div className="text-xs text-gray-400">
            Estado: <span className="text-white font-semibold uppercase">{conv.state}</span>
            {conv.metadata?.source_page && <p>Origen: <span className="font-mono">{conv.metadata.source_page}</span></p>}
          </div>
        </div>

        {/* Leads generated */}
        {data.leads?.length > 0 && (
          <div className="mt-6 p-4 bg-emerald-500/5 border border-emerald-500/30 rounded-xl">
            <p className="text-xs font-semibold text-emerald-300 mb-2 flex items-center gap-1">
              <ClipboardCheck className="w-3 h-3" /> Leads generados ({data.leads.length})
            </p>
            <ul className="space-y-1.5">
              {data.leads.map((l) => (
                <li key={l.id} className="text-xs text-emerald-100">
                  · <b>{l.name}</b> &lt;{l.email}&gt; · {l.source} · prio={l.priority || "normal"}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Transcript */}
        <div className="mt-6 space-y-3" data-testid="transcript">
          {data.messages.map((m) => <MsgRow key={m.id} m={m} />)}
        </div>
      </div>
    </StaffLayout>
  );
}

function MsgRow({ m }) {
  const time = m.created_at ? new Date(m.created_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";
  if (m.role === "user") {
    return (
      <div className="flex gap-2 justify-end" data-testid={`msg-user-${m.id}`}>
        <div className="max-w-[70%] p-3 bg-[#0B0B0B] border border-gray-700 rounded-lg text-sm text-white">
          {m.flagged && <p className="text-[9px] text-red-300 mb-1 uppercase">Posible injection</p>}
          <p className="whitespace-pre-wrap">{m.content}</p>
          <p className="text-[9px] text-gray-500 mt-1 text-right">{time}</p>
        </div>
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center">
          <User className="w-3.5 h-3.5 text-white" />
        </div>
      </div>
    );
  }
  if (m.role === "assistant") {
    return (
      <div className="flex gap-2" data-testid={`msg-assistant-${m.id}`}>
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#F26A21] flex items-center justify-center font-bold text-[#0B0B0B] text-xs">D</div>
        <div className="max-w-[70%] p-3 bg-[#111827] border border-gray-800 rounded-lg text-sm text-gray-200">
          <p className="whitespace-pre-wrap">{m.content}</p>
          <p className="text-[9px] text-gray-500 mt-1 flex justify-between">
            <span>{time}</span>
            {m.latency_ms && <span className="font-mono">{m.latency_ms}ms</span>}
          </p>
        </div>
      </div>
    );
  }
  if (m.role === "tool_call") {
    return (
      <div className="ml-9" data-testid={`msg-tool-call-${m.id}`}>
        <div className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded bg-[#F26A21]/10 text-[#F26A21] border border-[#F26A21]/30">
          <Sparkles className="w-3 h-3" /> tool_call → {m.tool_name}({tryJson(m.tool_arguments)})
          {m.pending && <span className="ml-1 text-amber-300">pending</span>}
          {m.cancelled && <span className="ml-1 text-red-300">cancelled</span>}
          {m.confirmed && <span className="ml-1 text-emerald-300">confirmed</span>}
        </div>
      </div>
    );
  }
  if (m.role === "tool_result") {
    const r = m.tool_result || {};
    return (
      <div className="ml-9" data-testid={`msg-tool-result-${m.id}`}>
        <div className={`inline-flex items-start gap-1 text-[10px] font-mono px-2 py-1 rounded border max-w-full ${
          r.ok ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-red-500/10 text-red-300 border-red-500/30"
        }`}>
          {r.ok
            ? <ClipboardCheck className="w-3 h-3 mt-0.5 flex-shrink-0" />
            : <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />}
          <span className="break-all">
            tool_result ← {m.tool_name}: {tryJson(r).slice(0, 200)}
          </span>
        </div>
      </div>
    );
  }
  return null;
}

function tryJson(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  try { return JSON.stringify(v); } catch { return String(v); }
}
